import React, { useEffect, useRef, useState } from 'react';
import Helpers from '../../../../utils/helper';
import './SellersEngagementDocuments.scss';
import SellersEngagementDocumentsSection from './SellersEngagementDocumentsSection';
import {
    DocumentSection,
    EngagementAgreementSectionName,
} from '../../../../slices/marketplace/timeline/models/document-section.model';
import { Button, Label } from 'reactstrap';
import { useStateSelector } from '../../../../store/selectors';
import { USER_ROLES } from '../../../../utils/constants';
import { DocumentDetailsStatus } from '../../../../slices/marketplace/timeline/models/enums/document-details-status.enum';
import { DocumentDetails } from '../../../../slices/marketplace/timeline/models/document-details.model';
import { ActionDocumentType } from '../../../../slices/marketplace/timeline/models/enums/action-document-type.enum';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import createNotification from '../../../../utils/createNotification';
import { EngagementDocument } from '../../../../slices/marketplace/timeline/models/engagement-document';
import ManualFileUploadPopup from './ManualFileUploadPopup/ManualFileUploadPopup';
import { Timeline } from '../../../../slices/marketplace/timeline/models/timeline.model';
import { useDispatch } from 'react-redux';
import { udateCurrentTimelineDocumentSections } from '../../../../slices/marketplace/timeline/timeline.slice';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { EngagementDocumentSource } from '../../../../slices/marketplace/timeline/models/enums/engagement-document-source';

interface SellersEngagementDocumentsProps {
    timelineId: number;
    currentEngagement: Timeline;
    preSelectedDocumentId?: string;
    resetPreSelectedDocumentId?(): void;
    isReadOnly: boolean;
}

const SellersEngagementDocuments = (props: SellersEngagementDocumentsProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;
    const documentSections: DocumentSection[] = [
        ...props.currentEngagement.engagementData.documentSectionsList,
    ].sort((a, b) => {
        if (
            a.name === EngagementAgreementSectionName &&
            b.name !== EngagementAgreementSectionName
        )
            return -1;
        if (
            b.name === EngagementAgreementSectionName &&
            a.name !== EngagementAgreementSectionName
        )
            return 1;

        return a.index - b.index;
    });
    const [selectedDocumentsIds, setSelectedDocumentsIds] = useState<string[]>(
        []
    );
    const timeline = useStateSelector((t) => t.timelines.currentTimeline);
    const [isSelectModeEnabled, setIsSelectModeEnabled] =
        useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const isAdmin = useStateSelector(
        (state) => state.userSummary.summary.roleId === USER_ROLES.ADMIN
    );
    const [isGettingForSign, setIsGettingForSign] = useState<boolean>(false);
    const [downloadableDocumentsIds, setDownloadableDocumentsIds] = useState<
        string[][]
    >([]);
    const [isManualFileUploadPopupVisible, setIsManualFileUploadPopupVisible] =
        useState<boolean>(false);
    const [documentSectionList, setDocumentSectionList] =
        useState<DocumentSection[]>(documentSections);
    const [isLoading, setIsLoading] = useState(true);
    const loadedSectionsCount = useRef(0);
    const [documentDetailsList, setDocumentDetailsList] = useState<
        DocumentDetails[]
    >([]);
    const party = useCurrentMarketplaceParty(props.timelineId);

    useEffect(() => {
        if (props.currentEngagement) {
            dispatch(udateCurrentTimelineDocumentSections(documentSectionList));
        }
    }, [documentSectionList]);

    useEffect(() => {
        setDocumentSectionList(
            props.currentEngagement.engagementData.documentSectionsList
        );
        setDocumentDetailsList(
            props.currentEngagement.engagementData?.documentsList
        );
    }, [props.currentEngagement?.engagementData?.documentsList]);

    const downloadableDocDetailsFilterCondition = (
        documentDetails: DocumentDetails | EngagementDocument
    ): boolean => {
        if (
            documentDetails.type === ActionDocumentType.ForSign &&
            documentDetails.status !== DocumentDetailsStatus.Completed
        ) {
            return false;
        }

        return (
            documentDetails.type !== ActionDocumentType.Readonly &&
            documentDetails.isPrepared &&
            documentDetails.source !== EngagementDocumentSource.Questionnaire
        );
    };

    const selectSection = () => {
        const areAllAvailableDocumentsSelected = () =>
            downloadableDocumentsIds.reduce(
                (total, row) => total + (row?.length ?? 0),
                0
            ) === selectedDocumentsIds.length;

        const fetchDocuments = async () => {
            const queryParams = selectedDocumentsIds
                .map((url) => `documentDetailsIds=${encodeURIComponent(url)}`)
                .join('&');
            const response = await axios.get(
                `/api/marketplace/engagements/${timeline.id}/download-documents?${queryParams}`,
                {
                    responseType: 'blob',
                }
            );
            return response;
        };

        const downloadFiles = async () => {
            setIsDownloading(true);
            const response = await fetchDocuments();
            if (response.status !== 200) {
                createNotification(
                    'An unexpected error occurred while downloading documents',
                    'error'
                );
                setIsDownloading(false);
                return;
            }
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const fileName =
                selectedDocumentsIds.length == 1
                    ? timeline.engagementData.documentsList.find(
                          (doc) => doc.id == selectedDocumentsIds[0]
                      )?.fileName
                    : 'EngagementDocuments.zip';
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setSelectedDocumentsIds([]);
            setIsSelectModeEnabled(false);
            setIsDownloading(false);
        };

        return (
            <div className="documents-btns-panel">
                {isSelectModeEnabled ? (
                    <div className="select-documents-btns-panel">
                        <Label className="selected-documents-count">
                            Selected: {selectedDocumentsIds.length}
                        </Label>
                        <Button
                            className="btn-cancel"
                            onClick={() => {
                                setIsSelectModeEnabled(false);
                                setSelectedDocumentsIds([]);
                            }}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => {
                                if (areAllAvailableDocumentsSelected()) {
                                    setSelectedDocumentsIds([]);
                                    return;
                                }
                                setSelectedDocumentsIds(
                                    downloadableDocumentsIds.reduce(
                                        (total, row) => total.concat(row)
                                    )
                                );
                            }}>
                            {areAllAvailableDocumentsSelected()
                                ? 'Unselect All'
                                : 'Select All'}
                        </Button>

                        <ButtonLoader
                            disabled={selectedDocumentsIds.length === 0}
                            onClick={() => downloadFiles()}
                            buttonText="Download Selected"
                            loaderButtonText={'Download Selected'}
                            isLoading={isDownloading}
                            className={
                                isDownloading
                                    ? 'btn btn-primary loader-submit'
                                    : 'btn btn-primary'
                            }/>
                    </div>
                ) : (
                    <Button
                        color="primary"
                        className="select-documents-btn"
                        onClick={() => setIsSelectModeEnabled(true)}>
                        Select For Download
                    </Button>
                )}
                {party === TimelineParty.VetValue ||
                party === TimelineParty.Broker ? (
                    <Button
                        color="primary"
                        className="select-documents-btn"
                        onClick={() => {
                            setIsManualFileUploadPopupVisible(true);
                        }}>
                        Add document
                    </Button>
                ) : (
                    []
                )}
            </div>
        );
    };

    const onDocumentSelected = (documentId: string) => {
        if (isSelectModeEnabled) {
            setSelectedDocumentsIds((prevSelectedDocumentsIds) => {
                if (prevSelectedDocumentsIds.includes(documentId)) {
                    return prevSelectedDocumentsIds.filter(
                        (id) => id !== documentId
                    );
                } else {
                    return [...prevSelectedDocumentsIds, documentId];
                }
            });
        }
    };

    return (
        <>
            {isLoading && Helpers.renderTableLoader()}
            <div className="sellers-engagement-documents" style={isLoading ? { display: 'none' } : undefined}>
                {documentSections.map((section, index) => {
                    return (
                        <SellersEngagementDocumentsSection
                            key={section.id}
                            buttonsSection={
                                index === 0 ? selectSection() : undefined
                            }
                            isSelectModeEnabled={isSelectModeEnabled}
                            selectedDocumentsIds={selectedDocumentsIds}
                            timelineId={props.timelineId}
                            engagementDocumenstSection={section}
                            onDocumentSelected={onDocumentSelected}
                            isAdminView={isAdmin}
                            isGettingForSign={isGettingForSign}
                            setIsGettingForSign={setIsGettingForSign}
                            downloadableDocDetailsFilterCondition={
                                downloadableDocDetailsFilterCondition
                            }
                            preSelectedDocumentId={props.preSelectedDocumentId}
                            resetPreSelectedDocumentId={
                                props.resetPreSelectedDocumentId
                            }
                            isReadOnly={props.isReadOnly}
                            setDownloadableDocumentsIds={(ids: string[]) => {
                                setDownloadableDocumentsIds((prevDocs) => {
                                    const docs = [...prevDocs];
                                    docs[index] = ids;
                                    return docs;
                                });
                            }}
                            onLoaded={() => {
                                loadedSectionsCount.current += 1;
                                if (loadedSectionsCount.current >= documentSections.length) {
                                    setIsLoading(false);
                                }
                            }}
                        />
                    );
                })}
            </div>
            {isManualFileUploadPopupVisible && (
                <ManualFileUploadPopup
                    engagementId={props.timelineId}
                    documentDetailsList={documentDetailsList}
                    isVisible={isManualFileUploadPopupVisible}
                    setIsVisible={setIsManualFileUploadPopupVisible}
                    documentsSections={documentSections}
                    sectionsModalMode={'external-modal'}
                />
            )}
        </>
    );
};

export default SellersEngagementDocuments;
